<script setup lang="ts">
import { ref } from "vue";

export type StatusMessage = {
  icon?: string;
  type: "error" | "warning" | "success" | "info";
  message: string;
};

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  id: {
    type: String,
    default: "",
  },
  label: {
    type: String,
  },
  placeholder: {
    type: String,
    default: "",
  },
  statusMessage: {
    type: Object as () => StatusMessage | undefined,
  },
  required: {
    type: Boolean,
    default: false,
  }
});
const emit = defineEmits(["update:modelValue", "change"]);

const isFocused = ref<boolean>(false);
const input = ref<string>(props.modelValue ?? "");

const oninput = (): void => {
  emit("update:modelValue", input.value);
};

const onChange = (): void => {
  emit("change", input.value);
};
</script>

<template>
  <div :class="['ui-textarea', props.statusMessage?.type]">
    <label v-if="props.label" :for="props.id">{{ props.label }}</label>
    <textarea :required="props.required" type="text" :id="props.id" :placeholder="props.placeholder" v-model="input" @input="oninput" @change="onChange" @focusin="isFocused = true" @blur="isFocused = false" />
    <span class="status-message" v-if="props.statusMessage">
      <Icon v-if="props.statusMessage.icon" :name="props.statusMessage.icon" />
      {{ props.statusMessage.message || "" }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.ui-textarea {
  display: flex;
  flex-direction: column;
  gap: 10px;

  label {
    color: rgb(var(--input-label-color));
    font-weight: 500;
    font-size: 15px;
    cursor: pointer;
  }

  textarea {
    padding: 10px 15px;
    background: rgb(var(--input-background));
    border: 1px solid rgba(var(--input-outline-color), 0);
    border-radius: var(--small-border-radius);
    color: rgb(var(--input-text-color));
    font-size: 18px;
    transition: border-color 0.2s;
    resize: vertical;
    min-height: fit-content;

    &::placeholder {
      color: rgba(var(--input-text-color), 0.4);
    }

    &:focus {
      border-color: rgba(var(--input-active-outline-color), 0.3);
      outline: none;
    }
  }

  .status-message {
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  &.error {
    textarea {
      border-color: rgba(var(--error-color), 0.3);
    }

    .status-message {
      color: rgb(var(--error-color));
    }
  }

  &.warning {
    textarea {
      border-color: rgba(var(--warning-color), 0.3);
    }

    .status-message {
      color: rgb(var(--warning-color));
    }
  }

  &.success {
    textarea {
      border-color: rgba(var(--success-color), 0.3);
    }

    .status-message {
      color: rgb(var(--success-color));
    }
  }

  &.info {
    textarea {
      border-color: rgba(var(--info-color), 0.3);
    }

    .status-message {
      color: rgb(var(--info-color));
    }
  }
}
</style>